import React, { createContext, useState, useContext, ReactNode } from 'react';

// Define the shape of our context
interface AppUtilsContextType {
  collapseSideBar: boolean;
  setCollapseSideBar: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context with a default value
const AppUtilsContext = createContext<AppUtilsContextType | undefined>(
  undefined,
);

// Create a custom hook for using the context
export const useAppUtilsContext = () => {
  const context = useContext(AppUtilsContext);
  if (context === undefined) {
    throw new Error(
      'useAppUtilsContext must be used within an AppUtilsContextProvider',
    );
  }
  return context;
};

// Props for the AppUtilsContextProvider component
interface AppUtilsContextProviderProps {
  children: ReactNode;
  initialCollapseSideBar?: boolean;
}

// Create a provider component
export const AppUtilsContextProvider: React.FC<
  AppUtilsContextProviderProps
> = ({ children, initialCollapseSideBar = false }) => {
  const [collapseSideBar, setCollapseSideBar] = useState(
    initialCollapseSideBar,
  );

  const value = React.useMemo(
    () => ({
      collapseSideBar,
      setCollapseSideBar,
    }),
    [collapseSideBar],
  );

  return (
    <AppUtilsContext.Provider value={value}>
      {children}
    </AppUtilsContext.Provider>
  );
};
